import { Platform } from '@angular/cdk/platform';
import { HttpHeaders } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { LoggingService } from '@cm/core/logging';
import { SettingsService } from '@cm/core/settings';
import { CmEventService } from '@cm/core/system';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IGNORE_ERRORS_BY_ID } from '../data/error-ignore-list';
import { CmFormErrorService } from '../services/cm-form-error.service';
import { CmInterceptHeaders } from './intercept-headers';
import { CmBaseUserService } from './user-service.class';

const ERROR_MESSAGES_TO_IGNORE: string[] = [
	'Failed to fetch',
	'cancelled',
	'Load failed',
	'NetworkError when attempting to fetch resource',
	'The network connection was lost',
	'已取消',
	'中止',
	'キャンセルしました',
	'Abgebrochen',
];

export function UnwrapErrorMessage(err: any, count: number = 0): string | null {
	if (count > 10) {
		return null;
	}

	if (Object.prototype.hasOwnProperty.call(err, 'message')) {
		return err.message.toString();
	} else if (typeof err === 'object') {
		return UnwrapErrorMessage(err, count + 1);
	} else if (typeof err === 'string') {
		return err;
	} else {
		return null;
	}
}

// Note: Changes in dependencies need to be reflected in the app.module implementation
export class CmErrorHandler implements ErrorHandler {
	constructor(
		private readonly logger: LoggingService,
		private readonly events: CmEventService,
		private readonly user: CmBaseUserService,
		private readonly forms: CmFormErrorService,
		private readonly settings: SettingsService,
		private readonly platform: Platform
	) {}

	/**
	 * Gathers the necessary data to log errors to the API
	 * @method
	 * @param {any} err - The error object to log
	 * @return {void}
	 */
	handleError(err: any): void {
		let message = UnwrapErrorMessage(err);
		if (!message) {
			try {
				message = JSON.stringify(err);
			} catch (ex) {
				return;
			}
		}
		const chunkFailedMessage = /Loading chunk [a-zA-Z0-9_ ]+ failed/;
		if (chunkFailedMessage.test(message)) {
			this.events.requireReload();
		}

		if (
			!err.doNotLog &&
			!this.isIgnoredErrorMessage(message) &&
			!chunkFailedMessage.test(message) &&
			!(err.networkError || {}).doNotLog &&
			(!err.id || (err.id && !IGNORE_ERRORS_BY_ID.includes(err.id)))
		) {
			combineLatest([this.user.user$, this.forms.formsToLog$, this.settings.client$])
				.pipe(
					map(([user, forms, client]) => {
						const browser = (({ appCodeName, appName, appVersion, userAgent, language }) => ({
							appCodeName,
							appName,
							appVersion,
							userAgent,
							language,
						}))(navigator);

						return {
							origin: (err || {}).raw || err,
							errors: err.errors ? err.errors : null,
							request: (err || {}).req || null,
							message,
							guid: err && err.id ? err.id : null,
							stack: err.stack ? err.stack : null,
							date: new Date(),
							browser,
							app: {
								portal: this.settings.portal,
								route: window.location.href,
								client: client,
								forms,
								platform: this.platform,
							},
							user: {
								username: (user || {}).username || '',
								email: (user || {}).email || '',
								isImpersonating: (user || {}).isImpersonating,
							},
						};
					}),
					take(1)
				)
				.subscribe(cmError => {
					if (this.settings.env.local) {
						console.group('Local Error Handler Details');
						console.log('Original error');
						console.error(err);
						console.log('Error sent to log');
						console.error(cmError);
						console.groupEnd();
					} else {
						this.logger.logToApiWithHeaders(
							cmError,
							new HttpHeaders({
								...CmInterceptHeaders.SuppressErrorLog,
							})
						);
					}
				});
		}
	}

	private isIgnoredErrorMessage(message: string): boolean {
		return ERROR_MESSAGES_TO_IGNORE.some(ignoreErrorMessage => message.includes(ignoreErrorMessage));
	}
}
